import { Add, Delete, List, LooksOneOutlined } from "@mui/icons-material";
import {
  Button,
  FormControl,
  FormLabel,
  Grid2 as Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import classNames from "classnames";
import _ from "lodash";
import React, { useState } from "react";
import { Controller, useFieldArray } from "react-hook-form";
import {
  ExtractionType,
  ParsingType,
  PredefinedExtraction,
  RegexMatch,
} from "../../../interfaces/doc";
import { ExtractionQuerySchemaType } from "../../../schemas/doc_config";
import FormMultiInputChipsField from "../../Common/FormMultiInputChipsField";
import FormTextField from "../../Common/FormTextField";
import ExtractionIcon from "./ConfigExtractionIcons";
import { useFormConfigContext } from "./ConfigFormContext";

interface ConfigExtractionQueryProps {
  index: number;
  groupIndex?: number;
  removeQuery: Function;
}

const ConfigExtractionQuery: React.FC<ConfigExtractionQueryProps> = ({
  index,
  groupIndex,
  removeQuery,
}) => {
  const { control, setValue, watch, getValues } = useFormConfigContext();
  const [open, setOpen] = useState<boolean>(false);
  const queryPath =
    groupIndex !== undefined
      ? (`config.extraction.queries.${index}.queries.${groupIndex}` as const)
      : (`config.extraction.queries.${index}` as const);

  const query = watch(`${queryPath}`) as ExtractionQuerySchemaType;

  const {
    fields: customFields,
    append: addField,
    remove: removeField,
  } = useFieldArray({
    control,
    name: `${queryPath}.custom_schema`,
  });

  const getContent = () => {
    if (query.type === ExtractionType.PREDEFINED)
      if (!open) {
        return (
          <>
            <div className="flex gap-2 w-full items-center justify-between">
              <Typography fontSize={16} fontWeight={600}>
                {query.name}
              </Typography>
              <div className="flex gap-2 items-center">
                <Typography fontWeight={200}>{query.type}</Typography>
                <Button color="error" onClick={() => removeQuery()}>
                  <Delete color="error" />
                </Button>
              </div>
            </div>
            <Typography fontSize={12}>{query.query}</Typography>
          </>
        );
      } else {
        return (
          <Stack rowGap={4}>
            <div className="flex gap-2">
              <FormTextField
                control={control}
                name={`${queryPath}.name`}
                placeholder="Nom de l'extraction"
                variant="standard"
                fullWidth
                slotProps={{
                  input: {
                    style: { fontSize: 20, fontWeight: 600 },
                  },
                }}
              />
              <Button onClick={() => setOpen(false)}>OK</Button>
            </div>
            <FormControl>
              <FormLabel id="query_label">Choix de l'extraction</FormLabel>
              <Controller
                control={control}
                name={`${queryPath}.query`}
                render={({ field }) => (
                  <Select
                    size="small"
                    className="flex flex-1"
                    labelId="query_label"
                    {...field}
                  >
                    {_.values(PredefinedExtraction).map((queryType) => (
                      <MenuItem key={queryType} value={queryType}>
                        {queryType}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Stack>
        );
      }
    else if (query.type === ExtractionType.LLM)
      if (!open) {
        return (
          <>
            <div className="flex gap-2 w-full items-center justify-between">
              <Typography fontSize={16} fontWeight={600}>
                {query.name}
              </Typography>
              <div className="flex gap-2 items-center">
                <Typography fontWeight={200}>{query.type}</Typography>
                <Button color="error" onClick={() => removeQuery()}>
                  <Delete color="error" />
                </Button>
              </div>
            </div>
            <div className="flex gap-2 w-full items-center">
              <ExtractionIcon
                parsingType={query.parsing_type ?? ParsingType.String}
                props={{ fontSize: "small" }}
              />
              {query.many && <List fontSize="small" />}
              <Typography fontSize={12} className="truncate">
                {query.query}
              </Typography>
            </div>
          </>
        );
      } else {
        return (
          <Stack rowGap={4}>
            <div className="flex gap-2">
              <FormTextField
                control={control}
                name={`${queryPath}.name`}
                placeholder="Nom de l'extraction"
                variant="standard"
                fullWidth
                slotProps={{
                  input: {
                    style: { fontSize: 20, fontWeight: 600 },
                  },
                }}
              />
              <Button onClick={() => setOpen(false)}>OK</Button>
            </div>
            <div className="flex gap-2 items-center">
              <Controller
                control={control}
                name={`${queryPath}.parsing_type`}
                render={({ field }) => (
                  <Select
                    size="small"
                    className="flex flex-1"
                    {...field}
                    onChange={(e) => {
                      if (e.target.value === ParsingType.Custom) {
                        setValue(`${queryPath}.custom_schema`, [
                          { name: "", parsing_type: ParsingType.String },
                        ]);
                      } else {
                        setValue(`${queryPath}.custom_schema`, undefined);
                      }
                      if (e.target.value === ParsingType.Enum) {
                        setValue(`${queryPath}.enum_values`, []);
                      } else {
                        setValue(`${queryPath}.enum_values`, undefined);
                      }
                      field.onChange(e.target.value);
                    }}
                  >
                    {_.values(ParsingType).map((parsingType) => (
                      <MenuItem key={parsingType} value={parsingType}>
                        <div className="flex items-center gap-1">
                          <ExtractionIcon
                            parsingType={parsingType}
                            props={{ fontSize: "small" }}
                          />
                          {parsingType}
                        </div>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormTextField
                className="flex"
                size="small"
                control={control}
                name={`${queryPath}.query`}
                multiline
                label="Extraction"
                placeholder="Décrivez l'information que vous souhaitez extraire"
              />
              <Controller
                name={`${queryPath}.many`}
                control={control}
                render={({ field: { onChange, value, ...props } }) => (
                  <div className="flex items-center gap-2 border rounded-lg p-1 w-fit">
                    <Button
                      {...props}
                      onClick={() => onChange(false)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded ${!value ? "bg-blue-100 text-blue-700" : "text-gray-600 hover:bg-gray-100"}`}
                    >
                      <LooksOneOutlined className="w-4 h-4" />
                      <span>Single</span>
                    </Button>
                    <Button
                      {...props}
                      onClick={() => onChange(true)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded ${value ? "bg-blue-100 text-blue-700" : "text-gray-600 hover:bg-gray-100"}`}
                    >
                      <List className="w-4 h-4" />
                      <span>Multiple</span>
                    </Button>
                  </div>
                )}
              />
            </div>
            {"parsing_type" in query &&
              query.parsing_type === ParsingType.Enum && (
                <FormMultiInputChipsField
                  size="small"
                  name={`${queryPath}.enum_values`}
                  control={control}
                  setValue={setValue}
                  getValues={getValues}
                  label="Options"
                />
              )}
            {"parsing_type" in query &&
              query.parsing_type === ParsingType.Custom && (
                <div className="flex flex-col gap-4 justify-center">
                  {customFields.map((field, fieldIndex) => {
                    const field_parsing_type = watch(
                      `${queryPath}.custom_schema.${fieldIndex}.parsing_type`
                    );
                    return (
                      <Grid
                        container
                        key={field.id}
                        spacing={2}
                        justifyContent="center"
                      >
                        <Grid size={6}>
                          <FormTextField
                            size="small"
                            label="Nom du champ"
                            name={`${queryPath}.custom_schema.${fieldIndex}.name`}
                            control={control}
                          />
                        </Grid>
                        <Grid size={4}>
                          <Controller
                            control={control}
                            name={`${queryPath}.custom_schema.${fieldIndex}.parsing_type`}
                            render={({ field }) => (
                              <Select
                                size="small"
                                className="flex flex-1"
                                {...field}
                              >
                                {[
                                  ParsingType.String,
                                  ParsingType.Int,
                                  ParsingType.Boolean,
                                  ParsingType.Float,
                                  ParsingType.Enum,
                                ].map((parsingType) => (
                                  <MenuItem
                                    key={parsingType}
                                    value={parsingType}
                                  >
                                    <div className="flex items-center gap-1">
                                      <ExtractionIcon
                                        parsingType={parsingType}
                                        props={{ fontSize: "small" }}
                                      />
                                      {parsingType}
                                    </div>
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />
                        </Grid>

                        {field_parsing_type === ParsingType.Enum && (
                          <Grid>
                            <FormMultiInputChipsField
                              size="small"
                              name={`${queryPath}.custom_schema.${fieldIndex}.enum_values`}
                              control={control}
                              setValue={setValue}
                              getValues={getValues}
                              label="Options"
                            />
                          </Grid>
                        )}
                        <Grid size={1}>
                          <Button
                            color="error"
                            onClick={() => removeField(fieldIndex)}
                          >
                            <Delete color="error" />
                          </Button>
                        </Grid>
                        <Grid size={1}>
                          {fieldIndex === customFields.length - 1 && (
                            <Button
                              color="info"
                              onClick={() =>
                                addField({
                                  name: "",
                                  parsing_type: ParsingType.String,
                                })
                              }
                              sx={{
                                display:
                                  fieldIndex !== customFields.length - 1
                                    ? "none"
                                    : "block",
                              }}
                            >
                              <Add />
                            </Button>
                          )}
                        </Grid>
                      </Grid>
                    );
                  })}
                </div>
              )}
          </Stack>
        );
      }
    else if (query.type === ExtractionType.REGEX)
      if (!open) {
        return (
          <>
            <div className="flex gap-2 w-full items-center justify-between">
              <Typography fontSize={16} fontWeight={600}>
                {query.name}
              </Typography>
              <div className="flex gap-2 items-center">
                <Typography fontWeight={200}>{query.type}</Typography>
                <Button color="error" onClick={() => removeQuery()}>
                  <Delete color="error" />
                </Button>
              </div>
            </div>
            <div className="flex gap-2 w-full items-center">
              <ExtractionIcon
                parsingType={query.parsing_type ?? ParsingType.String}
                props={{ fontSize: "small" }}
              />
              {query.many && <List fontSize="small" />}
              <Typography fontSize={12} className="truncate">
                {query.pattern}
              </Typography>
            </div>
          </>
        );
      } else {
        return (
          <Stack rowGap={4}>
            <div className="flex gap-2">
              <FormTextField
                control={control}
                name={`${queryPath}.name`}
                placeholder="Nom de l'extraction"
                variant="standard"
                fullWidth
                slotProps={{
                  input: {
                    style: { fontSize: 20, fontWeight: 600 },
                  },
                }}
              />
              <Button onClick={() => setOpen(false)}>OK</Button>
            </div>
            <div className="flex gap-2 items-center">
              <Controller
                control={control}
                name={`${queryPath}.parsing_type`}
                render={({ field }) => (
                  <Select
                    size="small"
                    className="flex flex-1"
                    {...field}
                    onChange={(e) => {
                      if (e.target.value === ParsingType.Custom) {
                        setValue(`${queryPath}.custom_schema`, [
                          { name: "", parsing_type: ParsingType.String },
                        ]);
                      } else {
                        setValue(`${queryPath}.custom_schema`, undefined);
                      }
                      if (e.target.value === ParsingType.Enum) {
                        setValue(`${queryPath}.enum_values`, []);
                      } else {
                        setValue(`${queryPath}.enum_values`, undefined);
                      }
                      field.onChange(e.target.value);
                    }}
                  >
                    {_.values(ParsingType).map((parsingType) => (
                      <MenuItem key={parsingType} value={parsingType}>
                        <div className="flex items-center gap-1">
                          <ExtractionIcon
                            parsingType={parsingType}
                            props={{ fontSize: "small" }}
                          />
                          {parsingType}
                        </div>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <FormTextField
                className="flex"
                size="small"
                control={control}
                name={`${queryPath}.pattern`}
                multiline
                label="Expression régulière"
              />
              <Controller
                name={`${queryPath}.many`}
                control={control}
                render={({ field: { onChange, value, ...props } }) => (
                  <div className="flex items-center gap-2 border rounded-lg p-1 w-fit">
                    <Button
                      {...props}
                      onClick={() => onChange(false)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded ${!value ? "bg-blue-100 text-blue-700" : "text-gray-600 hover:bg-gray-100"}`}
                    >
                      <LooksOneOutlined className="w-4 h-4" />
                      <span>Single</span>
                    </Button>
                    <Button
                      {...props}
                      onClick={() => onChange(true)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded ${value ? "bg-blue-100 text-blue-700" : "text-gray-600 hover:bg-gray-100"}`}
                    >
                      <List className="w-4 h-4" />
                      <span>Multiple</span>
                    </Button>
                  </div>
                )}
              />
            </div>
            {"parsing_type" in query &&
              query.parsing_type === ParsingType.Enum && (
                <FormMultiInputChipsField
                  size="small"
                  name={`${queryPath}.enum_values`}
                  control={control}
                  setValue={setValue}
                  getValues={getValues}
                  label="Options"
                />
              )}
            {"parsing_type" in query &&
              query.parsing_type === ParsingType.Custom && (
                <div className="flex flex-col gap-4 justify-center">
                  {customFields.map((field, fieldIndex) => {
                    const field_parsing_type = watch(
                      `${queryPath}.custom_schema.${fieldIndex}.parsing_type`
                    );
                    return (
                      <Grid
                        container
                        key={field.id}
                        spacing={2}
                        justifyContent="center"
                      >
                        <Grid size={6}>
                          <FormTextField
                            size="small"
                            label="Nom du champ"
                            name={`${queryPath}.custom_schema.${fieldIndex}.name`}
                            control={control}
                          />
                        </Grid>
                        <Grid size={4}>
                          <Controller
                            control={control}
                            name={`${queryPath}.custom_schema.${fieldIndex}.parsing_type`}
                            render={({ field }) => (
                              <Select
                                size="small"
                                className="flex flex-1"
                                {...field}
                              >
                                {[
                                  ParsingType.String,
                                  ParsingType.Int,
                                  ParsingType.Boolean,
                                  ParsingType.Float,
                                  ParsingType.Enum,
                                ].map((parsingType) => (
                                  <MenuItem
                                    key={parsingType}
                                    value={parsingType}
                                  >
                                    <div className="flex items-center gap-1">
                                      <ExtractionIcon
                                        parsingType={parsingType}
                                        props={{ fontSize: "small" }}
                                      />
                                      {parsingType}
                                    </div>
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />
                        </Grid>

                        {field_parsing_type === ParsingType.Enum && (
                          <Grid>
                            <FormMultiInputChipsField
                              size="small"
                              name={`${queryPath}.custom_schema.${fieldIndex}.enum_values`}
                              control={control}
                              setValue={setValue}
                              getValues={getValues}
                              label="Options"
                            />
                          </Grid>
                        )}
                        <Grid size={1}>
                          <Button
                            color="error"
                            onClick={() => removeField(fieldIndex)}
                          >
                            <Delete color="error" />
                          </Button>
                        </Grid>
                        <Grid size={1}>
                          {fieldIndex === customFields.length - 1 && (
                            <Button
                              color="info"
                              onClick={() =>
                                addField({
                                  name: "",
                                  parsing_type: ParsingType.String,
                                })
                              }
                              sx={{
                                display:
                                  fieldIndex !== customFields.length - 1
                                    ? "none"
                                    : "block",
                              }}
                            >
                              <Add />
                            </Button>
                          )}
                        </Grid>
                      </Grid>
                    );
                  })}
                </div>
              )}
            <Grid
              container
              spacing={2}
              justifyContent="center"
              alignItems="center"
            >
              <Grid size={6}>
                <Controller
                  control={control}
                  name={`${queryPath}.query`}
                  render={({ field }) => (
                    <Select
                      size="small"
                      labelId="query_label"
                      fullWidth
                      {...field}
                      onChange={(e) => {
                        if (e.target.value === RegexMatch.ALL) {
                          setValue(`${queryPath}.many`, true);
                        }
                        if (e.target.value === RegexMatch.EXISTS) {
                          setValue(
                            `${queryPath}.parsing_type`,
                            ParsingType.Boolean
                          );
                        }
                        field.onChange(e.target.value);
                      }}
                    >
                      {_.values(RegexMatch).map((match) => (
                        <MenuItem key={match} value={match}>
                          {match}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Grid>
              <Grid size={6}>
                <FormTextField
                  fullWidth
                  size="small"
                  label="Numéro du groupe"
                  name={`${queryPath}.params.group_number`}
                  handleChange={(e, fieldOnChange: (e: any) => void) =>
                    fieldOnChange(
                      e.target.value
                        ? Number(e.target.value.replace(/\D/, ""))
                        : null
                    )
                  }
                  control={control}
                  type="number"
                  rules={{ valueAsNumber: true }}
                />
              </Grid>
            </Grid>
          </Stack>
        );
      }
    else if (
      query.type === ExtractionType.COMPUTED ||
      query.type === ExtractionType.AGGREGATE
    )
      if (!open) {
        return (
          <>
            <div className="flex gap-2 w-full items-center justify-between">
              <Typography fontSize={16} fontWeight={600}>
                {query.name}
              </Typography>
              <div className="flex gap-2 items-center">
                <Typography fontWeight={200}>{query.type}</Typography>
                <Button color="error" onClick={() => removeQuery()}>
                  <Delete color="error" />
                </Button>
              </div>
            </div>
            <div className="flex gap-2 w-full items-center">
              <ExtractionIcon
                parsingType={query.parsing_type ?? ParsingType.String}
                props={{ fontSize: "small" }}
              />
              <Typography fontSize={12} className="truncate">
                {query.expression}
              </Typography>
            </div>
          </>
        );
      } else {
        return (
          <Stack rowGap={4}>
            <div className="flex gap-2">
              <FormTextField
                control={control}
                name={`${queryPath}.name`}
                placeholder="Nom de l'extraction"
                variant="standard"
                fullWidth
                slotProps={{
                  input: {
                    style: { fontSize: 20, fontWeight: 600 },
                  },
                }}
              />
              <Button onClick={() => setOpen(false)}>OK</Button>
            </div>
            <div className="flex gap-2">
              <Controller
                control={control}
                name={`${queryPath}.parsing_type`}
                render={({ field }) => (
                  <Select
                    size="small"
                    className="flex flex-1"
                    {...field}
                    onChange={(e) => {
                      if (e.target.value === ParsingType.Custom) {
                        setValue(`${queryPath}.custom_schema`, [
                          { name: "", parsing_type: ParsingType.String },
                        ]);
                      } else {
                        setValue(`${queryPath}.custom_schema`, undefined);
                      }
                      field.onChange(e.target.value);
                    }}
                  >
                    {_.values(ParsingType)
                      .filter((parsingType) => parsingType !== ParsingType.Enum)
                      .map((parsingType) => (
                        <MenuItem key={parsingType} value={parsingType}>
                          <div className="flex items-center gap-1">
                            <ExtractionIcon
                              parsingType={parsingType}
                              props={{ fontSize: "small" }}
                            />
                            {parsingType}
                          </div>
                        </MenuItem>
                      ))}
                  </Select>
                )}
              />
              <FormTextField
                className="flex"
                size="small"
                control={control}
                name={`${queryPath}.expression`}
                multiline
                label="Expression"
                placeholder="Décrivez l'information que vous souhaitez extraire en JSONata"
              />
            </div>
            {"parsing_type" in query &&
              query.parsing_type === ParsingType.Custom && (
                <div className="flex flex-col gap-4 justify-center">
                  {customFields.map((field, fieldIndex) => {
                    return (
                      <Grid
                        container
                        key={field.id}
                        spacing={2}
                        justifyContent="center"
                      >
                        <Grid size={6}>
                          <FormTextField
                            fullWidth
                            size="small"
                            label="Nom du champ"
                            name={`${queryPath}.custom_schema.${fieldIndex}.name`}
                            control={control}
                          />
                        </Grid>
                        <Grid size={4}>
                          <Controller
                            control={control}
                            name={`${queryPath}.custom_schema.${fieldIndex}.parsing_type`}
                            render={({ field }) => (
                              <Select
                                size="small"
                                className="flex flex-1"
                                {...field}
                              >
                                {[
                                  ParsingType.String,
                                  ParsingType.Int,
                                  ParsingType.Boolean,
                                  ParsingType.Float,
                                ].map((parsingType) => (
                                  <MenuItem
                                    key={parsingType}
                                    value={parsingType}
                                  >
                                    <div className="flex items-center gap-1">
                                      <ExtractionIcon
                                        parsingType={parsingType}
                                        props={{ fontSize: "small" }}
                                      />
                                      {parsingType}
                                    </div>
                                  </MenuItem>
                                ))}
                              </Select>
                            )}
                          />
                        </Grid>
                        <Grid size={1}>
                          <Button
                            color="error"
                            onClick={() => removeField(fieldIndex)}
                          >
                            <Delete color="error" />
                          </Button>
                        </Grid>
                        <Grid size={1}>
                          {fieldIndex === customFields.length - 1 && (
                            <Button
                              color="info"
                              onClick={() =>
                                addField({
                                  name: "",
                                  parsing_type: ParsingType.String,
                                })
                              }
                              sx={{
                                display:
                                  fieldIndex !== customFields.length - 1
                                    ? "none"
                                    : "block",
                              }}
                            >
                              <Add />
                            </Button>
                          )}
                        </Grid>
                      </Grid>
                    );
                  })}
                </div>
              )}
          </Stack>
        );
      }
    else return null;
  };

  return (
    <Paper
      square={false}
      elevation={3}
      className={classNames(
        "flex flex-col gap-2 p-2 border-[var(--mui-palette-primary-main)] border-solid	border-1",
        {
          "hover:cursor-pointer": !open,
        }
      )}
      onClick={!open ? () => setOpen(true) : undefined}
    >
      {getContent()}
    </Paper>
  );
};
export default ConfigExtractionQuery;
